import { Trash, Warning } from '@griply/assets';
import { ExternalConnection } from '@griply/types';
import {
  connectionNeedsContactsReconnect,
  isConnectionBroken,
} from '@griply/utils';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  CONTACT_SUGGESTIONS_ENABLED,
  useDeleteExternalConnection,
  useReconnectExternalConnection,
} from 'features/external-connections';
import React, { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { ExternalConnectionIcon } from 'shared/components/ui/external-connection-icon';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import {
  CalendarConnectionList,
  LoadingCalendarConnectionList,
} from './calendar-connection-list';
import * as Styled from './external-calendar-connection.style';

export type ExternalCalendarProps = {
  connection: ExternalConnection;
};

export const ExternalCalendarConnection: React.FC<ExternalCalendarProps> = ({
  connection,
}) => {
  const { t } = useTranslation();
  const {
    menuOpen: deleteDialogOpen,
    openMenu: openDeleteDialog,
    closeMenu: closeDeleteDialog,
  } = useOpenMenu();

  const deleteExternalConnection = useDeleteExternalConnection();
  const onDelete = () => deleteExternalConnection(connection.id);

  const { onReconnect, isPending } = useReconnectExternalConnection(connection);

  const broken = isConnectionBroken(connection);
  // No point offering a contacts reconnect while the suggestions themselves are switched off.
  const needsContactsReconnect =
    CONTACT_SUGGESTIONS_ENABLED && connectionNeedsContactsReconnect(connection);

  const [contactsNoticeOpen, setContactsNoticeOpen] = useState(false);
  const onToggleContactsNotice = () =>
    setContactsNoticeOpen((previous) => !previous);

  return (
    <>
      <Styled.Container>
        <Styled.Body>
          <Styled.IconContainer>
            <ExternalConnectionIcon provider={connection.provider} />
          </Styled.IconContainer>
          <Styled.Info>
            <Styled.EmailRow>
              <Styled.Email>{connection.providerInfo.email}</Styled.Email>
              {needsContactsReconnect && (
                <Styled.ContactsWarningButton
                  type="button"
                  onClick={onToggleContactsNotice}
                  aria-expanded={contactsNoticeOpen}
                  aria-label={t('calendar.accounts.contacts-permission')}
                >
                  <Icon icon={Warning} />
                </Styled.ContactsWarningButton>
              )}
            </Styled.EmailRow>
            {broken && (
              <Styled.BrokenMessage>
                {t('calendar.accounts.broken')}
              </Styled.BrokenMessage>
            )}
          </Styled.Info>
        </Styled.Body>

        {broken ? (
          <Button
            variant={Variants.Outlined}
            size={Sizes.Small}
            color={Colors.Inherit}
            onClick={onReconnect}
            disabled={isPending}
          >
            {t('calendar.accounts.reconnect')}
          </Button>
        ) : (
          <Suspense fallback={<LoadingCalendarConnectionList />}>
            <CalendarConnectionList
              connectionId={connection.id}
              blacklist={connection.settings?.calendarBlacklist}
            />
          </Suspense>
        )}

        <Styled.RemoveCell>
          <IconButton
            size={IconButtonSizes.Small}
            color={IconButtonColors.Default}
            icon={Trash}
            onClick={openDeleteDialog}
            aria-label={t(
              'forms.user-settings.sections.calendar.delete-external.dialog.title',
            )}
          />
        </Styled.RemoveCell>
      </Styled.Container>

      {needsContactsReconnect && contactsNoticeOpen && (
        <Styled.ContactsNotice>
          <Styled.ContactsNoticeIcon>
            <Icon icon={Warning} />
          </Styled.ContactsNoticeIcon>
          <Styled.ContactsNoticeMessage>
            {t('calendar.accounts.contacts-permission')}
          </Styled.ContactsNoticeMessage>
          <Button
            variant={Variants.Outlined}
            size={Sizes.Small}
            color={Colors.Primary}
            onClick={onReconnect}
            disabled={isPending}
          >
            {t('calendar.accounts.reconnect')}
          </Button>
        </Styled.ContactsNotice>
      )}

      {deleteDialogOpen && (
        <Dialog open onClose={closeDeleteDialog}>
          <Styled.DialogHeader>
            <Typography variant="h5" component="h1">
              {t(
                'forms.user-settings.sections.calendar.delete-external.dialog.title',
              )}
            </Typography>
          </Styled.DialogHeader>

          <Styled.DialogBody>
            <Typography variant="body2">
              {t(
                'forms.user-settings.sections.calendar.delete-external.dialog.description',
              )}
            </Typography>
          </Styled.DialogBody>

          <Styled.DialogFooter>
            <Button
              variant={Variants.Outlined}
              size={Sizes.Medium}
              color={Colors.Inherit}
              onClick={closeDeleteDialog}
            >
              {t(
                'forms.user-settings.sections.calendar.delete-external.dialog.buttons.cancel.label',
              )}
            </Button>
            <Button size={Sizes.Medium} color={Colors.Error} onClick={onDelete}>
              {t(
                'forms.user-settings.sections.calendar.delete-external.dialog.buttons.submit.label',
              )}
            </Button>
          </Styled.DialogFooter>
        </Dialog>
      )}
    </>
  );
};
